// MARK://dhk8/admin-shell/lib/clients/floor
// Editor del plano de mesas → dhk8-pos-floor.
//
// El backend modela el plano como FloorMap → { sections, tables } (un mapa default
// por tenant). El admin edita ese mapa default.
// F9.17 / ADR-0008: las escrituras de mesas/secciones van por el grafo (pos-floor)
//   createFloorTable / updateFloorTable / deleteFloorTable
//   createFloorSection / deleteFloorSection
// con el `mapId` resuelto por getLayout() (que también va por el grafo). El tenant
// lo impone el JWT/RLS (no va en el input). F6.2 R3c: el QR de mesa también va por
// el grafo (FloorTable.qrCode + regenerateTableQr).
// Adaptamos los DTOs del backend (TableDto/FloorSectionDto) ↔ los tipos de UI de
// mock-data (FloorTable/FloorSection). Preview: copia mutable en memoria.
import { requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import * as M from '../mock-data.js'
import { gqlClient } from '../gql.js'
import {
  AdminFloorMapsQuery,
  AdminCreateFloorTableMutation,
  AdminUpdateFloorTableMutation,
  AdminDeleteFloorTableMutation,
  AdminCreateFloorSectionMutation,
  AdminDeleteFloorSectionMutation,
  AdminRegenerateTableQrMutation,
} from '../queries/floor.queries.js'


// ── API DTOs (dhk8-pos-floor) ──────────────────────────────────────────────
interface ApiFloorSection { id: string; mapId: string; name: string; color: string | null; sortOrder: number }
interface ApiTable {
  id: string; mapId: string; sectionId: string | null; code: string; capacity: number
  shape: 'SQUARE' | 'ROUND' | 'RECT' | 'BOOTH'; posX: number; posY: number
  widthPx: number; heightPx: number; rotation: number
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING' | 'UNAVAILABLE'
  // Optional: the floor layout response may carry the table's QR token. The
  // QR-codes management screen reads it via `t.qrCode ?? null`.
  qrCode?: string | null
}
interface ApiFloorMap {
  id: string; tenantId: string; branchId: string | null; name: string
  widthPx: number; heightPx: number; isDefault: boolean; active: boolean
  createdAt: string; updatedAt: string
  sections: ApiFloorSection[]; tables: ApiTable[]
}

// ── QR de mesa (gestión back-office) ────────────────────────────────────────
// Cada mesa del backend trae un `qrCode` (string opaco). El comensal lo escanea
// y resuelve vía `GET /v1/floor/qr/:qrCode/resolve` (público, en el guest PWA).
// El admin gestiona el QR: ver / regenerar. `regenerateTableQrCode` →
// `POST /v1/floor/tables/:tableId/qr/regenerate`.
export interface TableQr {
  tableId: string
  code: string
  capacity: number
  sectionName: string | null
  qrCode: string | null
  status: ApiTable['status']
}

// ── adapters (API DTO ↔ UI type) ───────────────────────────────────────────
// The admin's UI only supports ROUND | RECT; anything else from the backend is
// shown as RECT. sectionId is required in the UI → fall back to '' (the page
// reassigns it on edit).
function toUiShape(s: ApiTable['shape']): 'ROUND' | 'RECT' { return s === 'ROUND' ? 'ROUND' : 'RECT' }
function toUiTable(t: ApiTable): M.FloorTable {
  return { id: t.id, code: t.code, capacity: t.capacity, posX: t.posX, posY: t.posY, widthPx: t.widthPx, heightPx: t.heightPx, shape: toUiShape(t.shape), sectionId: t.sectionId ?? '' }
}
function toUiSection(s: ApiFloorSection): M.FloorSection { return { id: s.id, name: s.name } }

export interface FloorLayout { mapId: string; widthPx: number; heightPx: number; sections: M.FloorSection[]; tables: M.FloorTable[] }

function toLayout(m: ApiFloorMap): FloorLayout {
  return { mapId: m.id, widthPx: m.widthPx, heightPx: m.heightPx, sections: m.sections.map(toUiSection), tables: m.tables.map(toUiTable) }
}

// ── mutable preview store ──────────────────────────────────────────────────
const db = {
  mapId: 'map-preview',
  sections: structuredClone(M.FLOOR_SECTIONS),
  tables: structuredClone(M.FLOOR_TABLES),
}
const id = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`

// `mapId` of the tenant's default map; resolved lazily by getLayout().
let cachedMapId: string | null = null
function requireMapId(): string {
  if (PREVIEW_STUB) return db.mapId
  if (!cachedMapId) throw new Error('Llama getLayout() antes de editar el plano')
  return cachedMapId
}

export async function getLayout(signal?: AbortSignal): Promise<FloorLayout> {
  if (PREVIEW_STUB) return mock({ mapId: db.mapId, widthPx: 920, heightPx: 640, sections: db.sections, tables: db.tables })
  if (requireTenant() === null) {
    return { mapId: '', widthPx: 920, heightPx: 640, sections: [], tables: [] }
  }
  // F9.5 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT, RLS). Mismo
  // campo floorMaps que ya consume el POS. Tomamos el mapa default del tenant
  // (isDefault) o el primero, y cacheamos su id para las escrituras (que también
  // van por el grafo desde F9.17).
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminFloorMapsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  const maps = res.data?.floorMaps ?? []
  const m = maps.find((x) => x.isDefault) ?? maps[0]
  if (!m) return { mapId: '', widthPx: 920, heightPx: 640, sections: [], tables: [] }
  cachedMapId = m.id
  return toLayout(m as unknown as ApiFloorMap)
}

export async function saveTable(table: M.FloorTable): Promise<M.FloorTable> {
  if (PREVIEW_STUB) {
    const i = db.tables.findIndex((x) => x.id === table.id)
    if (i >= 0) db.tables[i] = table; else db.tables.push(table)
    return mock(table, 60)
  }
  // F9.17 / ADR-0008: actualización por el grafo (tenant del JWT, RLS).
  const mapId = requireMapId()
  const input = {
    code: table.code, capacity: table.capacity, posX: table.posX, posY: table.posY,
    widthPx: table.widthPx, heightPx: table.heightPx, shape: table.shape,
    sectionId: table.sectionId ? table.sectionId : null,
  }
  const res = await gqlClient.mutation(AdminUpdateFloorTableMutation, { mapId, tableId: table.id, input }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.updateFloorTable
  if (!t) throw new Error('No se pudo actualizar la mesa')
  return toUiTable(t as unknown as ApiTable)
}

export async function createTable(input: Omit<M.FloorTable, 'id'>): Promise<M.FloorTable> {
  if (PREVIEW_STUB) { const tbl = { ...input, id: id('t') }; db.tables.push(tbl); return mock(tbl) }
  // F9.17 / ADR-0008: creación por el grafo (tenant del JWT, RLS).
  const mapId = requireMapId()
  const gqlInput: {
    code: string; posX: number; posY: number; capacity?: number; shape?: string
    widthPx?: number; heightPx?: number; sectionId?: string
  } = {
    code: input.code, posX: input.posX, posY: input.posY,
    capacity: input.capacity, shape: input.shape,
    widthPx: input.widthPx, heightPx: input.heightPx,
  }
  if (input.sectionId) gqlInput.sectionId = input.sectionId
  const res = await gqlClient.mutation(AdminCreateFloorTableMutation, { mapId, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.createFloorTable
  if (!t) throw new Error('No se pudo crear la mesa')
  return toUiTable(t as unknown as ApiTable)
}

export async function deleteTable(tableId: string): Promise<void> {
  if (PREVIEW_STUB) { db.tables = db.tables.filter((x) => x.id !== tableId); return mock(undefined) }
  // F9.17 / ADR-0008: borrado por el grafo (tenant del JWT, RLS).
  const mapId = requireMapId()
  const res = await gqlClient.mutation(AdminDeleteFloorTableMutation, { mapId, tableId }).toPromise()
  if (res.error) throw res.error
}

/** Mesas con su QR + sección, para la pantalla de gestión de QR. */
export async function listTableQrCodes(signal?: AbortSignal, branchId?: string | null): Promise<TableQr[]> {
  if (PREVIEW_STUB) {
    return mock(db.tables.map((t, i) => ({
      tableId: t.id, code: t.code, capacity: t.capacity,
      sectionName: db.sections.find((s) => s.id === t.sectionId)?.name ?? null,
      qrCode: `DEMOQR${(i + 1).toString().padStart(2, '0')}xxxxxx`,
      status: 'AVAILABLE' as ApiTable['status'],
    })))
  }
  if (requireTenant() === null) return []
  // F6.2 R3c / ADR-0008: lectura por el grafo (pos-floor, tenant del JWT, RLS).
  // floorMaps trae el plano default con FloorTable.qrCode (expuesto en R3c) —
  // sustituye el GET REST /v1/floor/layout.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminFloorMapsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  const maps = res.data?.floorMaps ?? []
  // 🔴 Antes: `maps.find(isDefault) ?? maps[0]` sobre TODOS los planos del
  // tenant (18 vienen marcados default) → se imprimían los QR de una sucursal
  // arbitraria. Ahora manda la sucursal elegida.
  const deseada = branchId ?? null
  const m = (deseada !== null ? maps.find((x) => x.branchId === deseada) : undefined)
    ?? maps.find((x) => x.isDefault) ?? maps[0]
  if (!m) return []
  cachedMapId = m.id
  const sectionName = (sid: string | null) => (m.sections ?? []).find((s) => s.id === sid)?.name ?? null
  return (m.tables ?? [])
    .map((t) => ({ tableId: t.id, code: t.code, capacity: t.capacity, sectionName: sectionName(t.sectionId ?? null), qrCode: t.qrCode ?? null, status: t.status as ApiTable['status'] }))
    .sort((a, b) => a.code.localeCompare(b.code, 'es', { numeric: true }))
}

/** Regenera el QR de una mesa. Devuelve el nuevo qrCode. */
export async function regenerateTableQr(tableId: string): Promise<string> {
  if (PREVIEW_STUB) return mock(`DEMOQR${Math.random().toString(36).slice(2, 10).toUpperCase()}`)
  // F6.2 R3c / ADR-0008: regenera el QR por el grafo (tenant del JWT, IDOR-guard
  // por RLS). Sustituye el POST REST /v1/floor/tables/:id/qr/regenerate.
  const res = await gqlClient.mutation(AdminRegenerateTableQrMutation, { tableId }).toPromise()
  if (res.error) throw res.error
  return res.data?.regenerateTableQr?.qrCode ?? ''
}

export async function createSection(name: string): Promise<M.FloorSection> {
  if (PREVIEW_STUB) { const s = { id: id('sec'), name }; db.sections.push(s); return mock(s) }
  // F9.17 / ADR-0008: creación de sección por el grafo (tenant del JWT, RLS).
  const mapId = requireMapId()
  const res = await gqlClient.mutation(AdminCreateFloorSectionMutation, { mapId, input: { name } }).toPromise()
  if (res.error) throw res.error
  const s = res.data?.createFloorSection
  if (!s) throw new Error('No se pudo crear la sección')
  return toUiSection(s as unknown as ApiFloorSection)
}

export async function deleteSection(sectionId: string): Promise<void> {
  if (PREVIEW_STUB) {
    db.sections = db.sections.filter((x) => x.id !== sectionId)
    db.tables = db.tables.filter((x) => x.sectionId !== sectionId)
    return mock(undefined)
  }
  // F9.17 / ADR-0008: borrado de sección por el grafo (tenant del JWT, RLS).
  const mapId = requireMapId()
  const res = await gqlClient.mutation(AdminDeleteFloorSectionMutation, { mapId, sectionId }).toPromise()
  if (res.error) throw res.error
}

/** Sucursales con plano, para el selector de la pantalla de QR. */
export async function listFloorBranches(signal?: AbortSignal): Promise<{ branchId: string; nombre: string }[]> {
  if (requireTenant() === null) return []
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminFloorMapsQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  const vistas = new Map<string, string>()
  for (const m of res.data?.floorMaps ?? []) {
    const b = (m as { branchId?: string | null }).branchId
    if (typeof b === 'string' && b.length > 0 && !vistas.has(b)) {
      vistas.set(b, (m.name ?? '').replace(/^plano\s+/i, '').trim() || m.name || b)
    }
  }
  return [...vistas].map(([branchId, nombre]) => ({ branchId, nombre }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
}
